import { Alert, Button } from "@mui/material";
import React, { PropsWithChildren, useEffect, useState } from "react";
import Spinner from "../common/components/Spinner";
import { createScopedLogger } from "../common/logger";
import ConfigContext, { Config } from "./ConfigContext";

const logger = createScopedLogger("ConfigContextProvider");

declare global {
  interface Window {
    __CONFIG__?: Config;
  }
}

export const ConfigContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [config, setConfig] = useState<Config>();
  const [isPending, setIsPending] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    // First check if config is available on the window object (injected at build time)
    const config = window.__CONFIG__;
    if (config) {
      setConfig(config);
      setLoadError(false);
      logger.debug("Config loaded from window", config);
      return;
    }

    // Fallback: fetch from /config.json
    async function loadConfig() {
      setIsPending(true);
      try {
        const r = await fetch("/config.json");
        if (!r.ok) {
          throw new Error(`Configuration request failed with status ${r.status}.`);
        }
        const c = (await r.json()) as Config;
        //throw new Error("Test error handling in ConfigContextProvider"); // Simulate error for testing
        //await delay(2000); // Simulate delay for testing purposes, remove later
        setConfig(c);
        setLoadError(false);
        logger.debug("Config loaded from /config.json", c);
      } catch (error) {
        logger.error("Failed to load runtime configuration", error);
        setLoadError(true);
      } finally {
        setIsPending(false);
      }
    }
    loadConfig();
  }, [retryCount]);

  if (isPending) {
    return <Spinner />;
  }
  if (config === undefined && loadError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => setRetryCount((count) => count + 1)}>
            Retry
          </Button>
        }
      >
        Runtime configuration could not be loaded.
      </Alert>
    );
  }
  if (config === undefined) {
    return <Spinner />;
  }
  return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>;
};

export default ConfigContextProvider;
